// ============================================================================
// ROLE ENDPOINTS
// ============================================================================
// Endpoint definitions for the Role Master module.
// BASE_PATH matches the backend controller route: RoleController → /Role

import { buildEndpoint, buildEndpointWithParams, buildEndpointWithQuery } from './base.endpoints';
import type { ID } from '../types';

/**
 * Base path for role endpoints (RoleController → api/v1/[controller])
 */
const BASE_PATH = '/Role';

/**
 * Role Endpoints
 */
export const roleEndpoints = {
  /**
   * Get all roles
   * GET /Role
   */
  list: () => buildEndpoint(BASE_PATH),

  /**
   * Get role by ID
   * GET /Role/:id
   */
  getById: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * Get role by code
   * GET /Role/code/:code
   */
  getByCode: (code: string) => buildEndpointWithParams(`${BASE_PATH}/code/:code`, { code }),

  /**
   * Get roles by type (system, tenant, custom, ...)
   * GET /Role/type/:roleType
   */
  getByType: (roleType: string) => buildEndpointWithParams(`${BASE_PATH}/type/:roleType`, { roleType }),

  /**
   * Get roles belonging to an application
   * GET /Role/app/:appId
   */
  getByAppId: (appId: ID) => buildEndpointWithParams(`${BASE_PATH}/app/:appId`, { appId }),

  /**
   * Create a new role
   * POST /Role
   */
  create: () => buildEndpoint(BASE_PATH),

  /**
   * Update an existing role
   * PUT /Role/:id
   */
  update: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * Delete a role (hard delete on the backend)
   * DELETE /Role/:id
   */
  delete: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  // ── Keycloak synchronisation ──────────────────────────────────────────

  /**
   * Sync a single role to Keycloak as a CLIENT role
   * POST /Role/:id/sync-to-keycloak?realmName&clientId
   */
  syncToKeycloak: (id: ID, realmName: string, clientId: string) =>
    buildEndpointWithQuery(
      buildEndpointWithParams(`${BASE_PATH}/:id/sync-to-keycloak`, { id }),
      { realmName, clientId },
    ),

  /**
   * Sync all roles to Keycloak as CLIENT roles
   * POST /Role/sync-all-to-keycloak?realmName&clientId
   */
  syncAllToKeycloak: (realmName: string, clientId: string) =>
    buildEndpointWithQuery(buildEndpoint(`${BASE_PATH}/sync-all-to-keycloak`), { realmName, clientId }),

  /**
   * List CLIENT roles held in Keycloak
   * GET /Role/keycloak/:realmName?clientId
   */
  getKeycloakRoles: (realmName: string, clientId: string) =>
    buildEndpointWithQuery(
      buildEndpointWithParams(`${BASE_PATH}/keycloak/:realmName`, { realmName }),
      { clientId },
    ),

  /**
   * Import CLIENT roles from Keycloak into the database
   * POST /Role/sync-from-keycloak?realmName&clientId&appId
   */
  syncFromKeycloak: (realmName: string, clientId: string, appId?: ID) =>
    buildEndpointWithQuery(buildEndpoint(`${BASE_PATH}/sync-from-keycloak`), {
      realmName,
      clientId,
      ...(appId ? { appId } : {}),
    }),

  /**
   * Check whether a role exists in both the database and Keycloak
   * GET /Role/validate/:roleCode?realmName&clientId
   */
  validate: (roleCode: string, realmName: string, clientId?: string) =>
    buildEndpointWithQuery(
      buildEndpointWithParams(`${BASE_PATH}/validate/:roleCode`, { roleCode }),
      { realmName, ...(clientId ? { clientId } : {}) },
    ),

  // ── Scope assignments (tenant-scoped) ─────────────────────────────────

  /**
   * Get scope assignments (entities / BUs / financial years) for a role
   * GET /Role/:roleId/scope-assignments
   */
  scopeAssignments: (roleId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:roleId/scope-assignments`, { roleId }),

  /**
   * Get only the defaults summary for a role
   * GET /Role/:roleId/scope-defaults
   */
  scopeDefaults: (roleId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:roleId/scope-defaults`, { roleId }),

  /**
   * Update which entity / BU / FY is the default for a role
   * PUT /Role/:roleId/scope-defaults
   */
  updateScopeDefault: (roleId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:roleId/scope-defaults`, { roleId }),

  /**
   * Assign scopes to a role
   * PUT /Role/:roleId/scope-assignments
   */
  assignScopes: (roleId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:roleId/scope-assignments`, { roleId }),

  /**
   * Remove all scope assignments for a role
   * DELETE /Role/:roleId/scope-assignments
   */
  removeScopeAssignments: (roleId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:roleId/scope-assignments`, { roleId }),
} as const;

/**
 * Export endpoints
 */
export default roleEndpoints;
